import {
    ALIGNMENT,
    type ApplicationSettingAuthenticationField,
    type DataSourceAbstract,
    type Field,
    type FieldBreakPointConfigProtocol,
    type FieldCellValue
} from '@lighthouse/core'
import { reduce } from 'rambda'

import { themeFieldDesignConfig } from '../../constants'
import { getEmptyCellValue, getFieldInputTypeByFieldType, getTranslation } from '../../utils'
import type { FieldInputConfig } from '../FieldInput'
import { commonFieldInputConfig } from './constant'

export const getFields = (fields: ApplicationSettingAuthenticationField[], dataSource?: DataSourceAbstract) =>
    reduce<ApplicationSettingAuthenticationField, (ApplicationSettingAuthenticationField & { value: FieldCellValue })[]>(
        (pre, item) => {
            const field = dataSource?.schema?.[item.fieldId]
            return field ? [...pre, { ...item, value: getEmptyCellValue(field?.type ?? 'text') }] : pre
        },
        [],
        fields
    )

export const getFieldInputConfig = (params: { field?: Field; config: ApplicationSettingAuthenticationField }): FieldInputConfig => {
    const { config, field } = params
    const { fieldId, title, placeHolder, required } = config
    switch (field?.type) {
        case 'role': {
            const baseDesign = themeFieldDesignConfig('relativeSelect').LIGHT || {}
            const breakPoint: FieldBreakPointConfigProtocol = {
                id: '',
                size: {
                    width: {
                        size: 'auto'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                ...baseDesign
            }
            return {
                fieldPointer: fieldId,
                placeholder: placeHolder ?? getTranslation('pleaseFillOut'),
                title,
                required,
                ...commonFieldInputConfig,
                inputType: 'relativeSelect',
                relativeSelect: {
                    align: ALIGNMENT.left,
                    showType: 'list',
                    viewFieldSettings: [],
                    direction: 'vertical',
                    showMode: 'input',
                    relativePointer: 'ROLE_DATASOURCE',
                    relativeFieldPointer: 'ID',
                    relativeShowFieldPointer: 'ROLE_NAME'
                },
                breakPoint
            }
        }
        case 'department': {
            const baseDesign = themeFieldDesignConfig('cascade').LIGHT || {}
            const breakPoint: FieldBreakPointConfigProtocol = {
                id: '',
                size: {
                    width: {
                        size: 'auto'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                ...baseDesign
            }
            return {
                fieldPointer: fieldId,
                placeholder: placeHolder ?? getTranslation('pleaseFillOut'),
                title,
                required,
                ...commonFieldInputConfig,
                inputType: 'cascade',
                cascade: {
                    cascadeFieldPointer: 'ID',
                    cascadeShowFieldPointer: 'DEPARTMENT_NAME',
                    cascadePointer: 'DEPARTMENT_DATASOURCE',
                    parentFieldPointer: 'PARENT_DEPARTMENT',
                    isLastLevel: false,
                    showPath: true
                },
                breakPoint
            }
        }
        default: {
            const inputType = getFieldInputTypeByFieldType(field?.type ?? 'text')
            const baseDesign = themeFieldDesignConfig(inputType).LIGHT || {}
            const breakPoint: FieldBreakPointConfigProtocol = {
                id: '',
                size: {
                    width: {
                        size: 'auto'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                ...baseDesign
            }
            return {
                fieldPointer: fieldId,
                placeholder: placeHolder ?? getTranslation('pleaseFillOut'),
                title,
                required,
                ...commonFieldInputConfig,
                inputType,
                breakPoint
            } as FieldInputConfig
        }
    }
}
